import React, { useState, useEffect, useCallback } from 'react';
import eventLogService from '../../services/eventLogService';
import { US_STATES } from '../../utils/constants';
import { formatPhoneForDisplay } from '../../utils/phoneUtils';
import { formatPerformedOnLabel, formatPerformedByLabel, formatActionLabel, normalizeEventLogAction, formatEventLogDateTime, moduleDisplayName, parseEventLogJson } from '../../utils/eventLogDisplayUtils';
import ListScreenPaginationBar from '../Common/ListScreenPaginationBar/ListScreenPaginationBar';
import './EventLogSection.css';

const PHONE_LOG_KEYS = new Set([
    'phone', 'cell_phone', 'fax', 'home_phone', 'phone_number',
    'phone_1', 'phone_2', 'fax_1', 'fax_2', 'phone_secondary_1', 'phone_secondary_2'
]);

const PRACTICE_FIELD_LABELS = {
    tax_id: 'Tax ID',
    rpm_start_date: 'RPM Start Date',
    rpm_end_date: 'RPM End Date',
    billing_from_date: 'Billing From Date',
    billing_to_date: 'Billing To Date',
    enrolled_date: 'Enrolled Date',
    type_of_monitoring: 'Type of Monitoring',
    buying_listing_device: 'Buying Device',
    leasing_listing_device: 'Leasing Device',
    evitals_monitoring: 'eVitals Monitoring',
    rpm_validity_days: 'Billing Period Days',
    email_1: 'Email',
    email_2: 'Secondary Email',
    phone_1: 'Phone',
    phone_2: 'Phone 2',
    fax_1: 'Fax',
    fax_2: 'Secondary Fax',
    address_1: 'Address',
    address_2: 'Secondary Address',
    provider: 'Provider',
    practice_caregiver: 'Practice Caregiver',
    admin: 'Admin',
    system_caregiver: 'System Caregiver'
};

const formatLogDateValue = (val) => {
    if (val == null || val === '') return val;
    if (val instanceof Date && !Number.isNaN(val.getTime())) {
        return val.toISOString().slice(0, 10);
    }
    const str = String(val).trim();
    const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : val;
};

const formatFieldLabel = (key) => {
    if (PRACTICE_FIELD_LABELS[key]) return PRACTICE_FIELD_LABELS[key];
    if (key === 'phone' || key === 'home_phone' || key === 'phone_number') return 'Phone Number';
    if (key === 'cell_phone') return 'Cell Phone';
    if (key === 'state_id' || key === 'state' || key === 'state_1') return 'State';
    if (key === 'is_two_way_auth' || key === 'two_way_auth') return 'Two Way Auth';
    if (key === 'mobile_app_access' || key === 'mobileAppAccess') return 'Mobile App Access';
    if (key === 'title_id' || key === 'title') return 'Title';
    if (key === 'gender_id' || key === 'gender') return 'Gender';
    if (key === 'model_number' || key === 'modelNumber') return 'Model Number';
    if (key === 'serial_number' || key === 'serialNumber') return 'Serial Number';
    if (key === 'gateway_serial_number' || key === 'gatewaySerialNumber') return 'Gateway Serial Number';
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const isMeterMappingRow = (row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    return (
        row.model_number != null ||
        row.modelNumber != null ||
        row.serial_number != null ||
        row.serialNumber != null ||
        row['Model number'] != null ||
        row['Serial number'] != null
    );
};

const meterRowKey = (row) => {
    const serial = String(row?.serial_number ?? row?.serialNumber ?? row?.['Serial number'] ?? '').trim().toLowerCase();
    const model = String(row?.model_number ?? row?.modelNumber ?? row?.['Model number'] ?? '').trim().toLowerCase();
    const ext = String(row?.extension ?? row?.Extension ?? '').trim().toLowerCase();
    return `${serial}|${model}|${ext}`;
};

const normalizeMeterRowFields = (row) => {
    if (!row || typeof row !== 'object') return {};
    return {
        model_number: row.model_number ?? row.modelNumber ?? row['Model number'] ?? '',
        extension: row.extension ?? row.Extension ?? '',
        serial_number: row.serial_number ?? row.serialNumber ?? row['Serial number'] ?? '',
        gateway_serial_number:
            row.gateway_serial_number ??
            row.gatewaySerialNumber ??
            row['Gateway serial number'] ??
            '',
    };
};

const EventLogSection = ({ moduleId, targetUserId, practiceId, patientId = null, sectionFilter, allModules = false, modulePrefix = null }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState('10');
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [error, setError] = useState(null);

    const pageLimit = entriesPerPage === 'all' ? 1000 : parseInt(entriesPerPage, 10) || 10;

    const fetchLogs = useCallback(async () => {
        if (!allModules && !moduleId && !modulePrefix) return;

        setLoading(true);
        setError(null);
        try {
            const response = await eventLogService.getLogs(allModules ? null : moduleId, targetUserId ?? null, {
                page,
                limit: pageLimit,
                ...(practiceId != null && { practiceId }),
                ...(patientId != null && patientId !== '' && { patientId }),
                ...(sectionFilter && { section: sectionFilter }),
                ...(modulePrefix && { modulePrefix })
            });

            if (response && response.success) {
                setLogs(response.logs ?? []);
                setTotalPages(response.pagination?.total_pages ?? 1);
                setTotalLogs(response.pagination?.total ?? 0);
            } else {
                setLogs([]);
                setTotalPages(1);
                setTotalLogs(0);
            }
        } catch (err) {
            console.error('Failed to fetch logs:', err);
            setError('Unable to load event logs.');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [moduleId, targetUserId, practiceId, patientId, page, pageLimit, sectionFilter, allModules, modulePrefix]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handleEntriesChange = (value) => {
        setEntriesPerPage(value);
        setPage(1);
    };

    const formatDate = (dateString) => formatEventLogDateTime(dateString);

    /**
     * Finds differences between old and new data objects and returns an array of comparison rows.
     */
    /**
     * Finds differences between old and new data objects and returns an array of comparison rows.
     */
    const getComparisonRows = (oldData, newData, moduleType, action) => {
        const normalizedAction = normalizeEventLogAction(action);
        const actionLower = normalizedAction || String(action || '').toLowerCase();
        if (!newData && actionLower !== 'delete') return [];
        const parsedOld = typeof oldData === 'string' ? JSON.parse(oldData || '{}') : (oldData || {});
        const parsedNew = typeof newData === 'string' ? JSON.parse(newData || '{}') : (newData || {});

        // If parsedNew is actually a "changes" object from backend (keys having from/to)
        // we check if it has 'from' and 'to' in its keys.
        const isChangesObject = Object.values(parsedNew).some(val => val && typeof val === 'object' && ('from' in val || 'to' in val));

        const rows = [];

        // Helper to format 24h time string to 12h format
        const formatTime12 = (val) => {
            if (!val || typeof val !== 'string') return val;
            // Match HH:mm or HH:mm:ss
            const timeMatch = val.match(/^(\d{1,2}):(\d{2})(:(\d{2}))?$/);
            if (!timeMatch) return val;
            
            let hours = parseInt(timeMatch[1]);
            const minutes = timeMatch[2];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // 0 becomes 12
            return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
        };

        // Helper to normalize and map values
        const normalize = (key, val) => {
            const boolKeys = ['is_two_way_auth', 'two_way_auth', 'mobile_app_access', 'mobileAppAccess', 'is_foracare', 'is_withings', 'is_default', 'is_online', 'agreement', 'status'];
            
            // Empty / missing values are always N/A (never "Disabled") so create logs
            // show N/A → actual value instead of Disabled → Disabled/Active.
            if (val === null || val === undefined || String(val).trim() === '' || (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) || (Array.isArray(val) && val.length === 0)) {
                return 'N/A';
            }
            
            // Map Title
            if (key === 'title_id' || key === 'title') {
                const titleMap = { 1: 'Dr.', 2: 'Mr.', 3: 'Mrs.', 4: 'Ms.', '1': 'Dr.', '2': 'Mr.', '3': 'Mrs.', '4': 'Ms.' };
                return titleMap[val] || String(val);
            }

            // Map Gender
            if (key === 'gender' || key === 'gender_id') {
                const genderMap = { 1: 'Male', 2: 'Female', 3: 'Other', 0: 'N/A', '1': 'Male', '2': 'Female', '3': 'Other', '0': 'N/A' };
                return genderMap[val] || String(val);
            }

            // Map State IDs / names to state names
            if (key === 'state_id' || key === 'state' || key === 'state_1') {
                const asNum = parseInt(String(val), 10);
                if (Number.isFinite(asNum) && String(asNum) === String(val).trim()) {
                    const stateObj = US_STATES.find((s) => s.id === asNum);
                    return stateObj ? stateObj.name : String(val);
                }
                return String(val);
            }

            // Normalize phone/fax values so formatted vs digits compare equal
            if (PHONE_LOG_KEYS.has(key)) {
                return formatPhoneForDisplay(val, 'N/A');
            }

            if (String(key).endsWith('_date') || (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val))) {
                const dated = formatLogDateValue(val);
                return dated == null || dated === '' ? 'N/A' : String(dated);
            }

            // Map Booleans
            if (boolKeys.includes(key)) {
                if (key === 'status' && typeof val === 'string') {
                    return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
                }
                const isEnabled = val === 1 || val === '1' || val === true || String(val).toLowerCase() === 'enabled' || String(val).toLowerCase() === 'true';
                return isEnabled ? 'Enabled' : 'Disabled';
            }

            // Time Formatting (if key suggests it's a time field)
            if (key.toLowerCase().includes('time') || key.toLowerCase().includes('period')) {
                return formatTime12(String(val));
            }

            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
        };

        const skipKeys = [
            'id', 'password', 'created_at', 'updated_at', 'deleted_at', 
            'performer_name', 'role_id', 'practice_id', 'practice_ids', 'practices',
            'practice_patient_assignments', 'user_id', 'users_id',
            'email_verified_at', 'remember_token', 
            'session_time', 'last_login', 'agreement_date', 'ip_address',
            'admin_details', 'admin_password_updated'
        ];

        const scheduleArrayKeys = [
            'dailySchedule', 'bloodPressureTimePeriod', 'bloodGlucoseTimePeriod', 'weightTimePeriod', 
            'bloodPressureTargetRange', 'bloodGlucoseTargetRange', 'weightTargetRange'
        ];

        const isCreateLog = actionLower === 'create';

        const processChange = (key, oldVal, newVal) => {
            if (skipKeys.includes(key)) return;
            const isPracticeModule = String(moduleType || '').toLowerCase() === 'practice';
            if (isPracticeModule && (key === 'admin_details' || key === 'admin_password_updated' || String(key).startsWith('admin_'))) {
                return;
            }

            // Create: always Old = N/A, New = actual value (Two Way Auth / Mobile App / Status, etc.)
            if (isCreateLog) {
                const createdVal = newVal !== undefined && newVal !== null && !(typeof newVal === 'string' && newVal.trim() === '')
                    ? newVal
                    : oldVal;
                const newNorm = normalize(key, createdVal);
                if (newNorm === 'N/A' && (createdVal === null || createdVal === undefined || createdVal === '')) {
                    return;
                }
                rows.push({
                    section: 'General',
                    label: formatFieldLabel(key),
                    oldVal: 'N/A',
                    newVal: newNorm
                });
                return;
            }

            // Robust Array Diffing for Schedule
            const isScheduleArray = scheduleArrayKeys.includes(key);
            
            if (isScheduleArray || (Array.isArray(oldVal) && Array.isArray(newVal))) {
                const arr1 = Array.isArray(oldVal) ? oldVal : [];
                const arr2 = Array.isArray(newVal) ? newVal : [];
                const length = Math.max(arr1.length, arr2.length);
                
                for (let i = 0; i < length; i++) {
                    const itemOld = arr1[i] || {};
                    const itemNew = arr2[i] || {};
                    const itemName = itemNew.name || itemNew.label || itemOld.name || itemOld.label || `Item ${i+1}`;
                    const allItemFields = new Set([...Object.keys(itemOld), ...Object.keys(itemNew)]);
                    
                        allItemFields.forEach(field => {
                        if (['id', 'name', 'label'].includes(field)) return;
                        const v1 = itemOld[field];
                        const v2 = itemNew[field];
                        
                        // Strict Normalized Comparison
                        const norm1 = normalize(field, v1);
                        const norm2 = normalize(field, v2);

                        if (norm1 !== norm2) {
                            let fieldLabel = field.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                            fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
                            
                            let sectionName = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                            sectionName = sectionName.charAt(0).toUpperCase() + sectionName.slice(1);

                            rows.push({
                                section: sectionName,
                                label: `${itemName} - ${fieldLabel}`,
                                oldVal: norm1,
                                newVal: norm2
                            });
                        }
                    });
                }
                return;
            }


            // Handle Objects (like Weight Target Range if not an array)
            if (typeof oldVal === 'object' || typeof newVal === 'object') {
                if (oldVal !== null || newVal !== null) {
                    const obj1 = (typeof oldVal === 'object' && oldVal !== null && !Array.isArray(oldVal)) ? oldVal : {};
                    const obj2 = (typeof newVal === 'object' && newVal !== null && !Array.isArray(newVal)) ? newVal : {};
                    const subKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
                    
                    if (subKeys.size > 0 && !isScheduleArray) {
                         subKeys.forEach(subKey => {
                            const v1 = obj1[subKey];
                            const v2 = obj2[subKey];
                            
                            const norm1 = normalize(subKey, v1);
                            const norm2 = normalize(subKey, v2);

                            if (norm1 !== norm2) {
                                let label = subKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
                                label = label.charAt(0).toUpperCase() + label.slice(1);
                                rows.push({
                                    section: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                    label: label,
                                    oldVal: norm1,
                                    newVal: norm2
                                });
                            }
                        });
                        return;
                    }
                }
            }

            const v1Norm = normalize(key, oldVal);
            const v2Norm = normalize(key, newVal);

            if (v1Norm !== v2Norm) {
                const label = formatFieldLabel(key);
                
                rows.push({ 
                    section: 'General',
                    label, 
                    oldVal: v1Norm, 
                    newVal: v2Norm 
                });
            }
        };

        if (actionLower === 'delete') {
            const delRows = [];
            const delSnap =
                parsedOld && typeof parsedOld === 'object' && Object.keys(parsedOld).length ? parsedOld : {};
            const inventoryKeys = [
                'id',
                'product_name',
                'model_number',
                'product_category',
                'qty',
                'price',
                'status',
                'received_date',
                'gateway_serial_number'
            ];
            const looksLikeInventoryDelete = ['product_name', 'model_number', 'product_category'].some(
                (k) => delSnap[k] != null && delSnap[k] !== ''
            );
            if (looksLikeInventoryDelete) {
                inventoryKeys.forEach((key) => {
                    if (delSnap[key] === undefined || delSnap[key] === null || delSnap[key] === '') return;
                    delRows.push({
                        section: 'Removed product',
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                        oldVal: normalize(key, delSnap[key]),
                        newVal: '—'
                    });
                });
            }
            if (!delRows.length && parsedNew && typeof parsedNew === 'object' && looksLikeInventoryDelete) {
                ['product_id', 'product_name', 'model_number', 'product_category'].forEach((key) => {
                    if (parsedNew[key] === undefined || parsedNew[key] === null || parsedNew[key] === '') return;
                    delRows.push({
                        section: 'Removed product',
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
                        oldVal: '—',
                        newVal: normalize(key, parsedNew[key])
                    });
                });
            }

            // Practice / Patient / Provider / Caregiver / Admin / etc.: show snapshot from `data` (oldData)
            const skipDeleteDisplay = new Set([
                'password',
                'remember_token',
                'email_verified_at',
                'session_time',
                'last_login',
                'created_at',
                'updated_at',
                'deleted_at',
                'agreement_date',
                'ip_address',
                'performer_name',
                'practice_id',
                'practice_ids',
                'role_id',
                'user_id',
                'users_id',
                'outcome'
            ]);
            Object.keys(delSnap).forEach((key) => {
                if (inventoryKeys.includes(key)) return;
                if (skipDeleteDisplay.has(key)) return;
                const val = delSnap[key];
                if (val === undefined || val === null || val === '') return;
                if (typeof val === 'object' && !Array.isArray(val)) {
                    if (val instanceof Date) {
                        /* fall through to stringify below */
                    } else if (Object.keys(val).length === 0) {
                        return;
                    }
                }
                let displayVal;
                if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
                    displayVal = JSON.stringify(val);
                } else {
                    displayVal = normalize(key, val);
                }
                delRows.push({
                    section: 'Deleted record',
                    label: formatFieldLabel(key),
                    oldVal: displayVal,
                    newVal: '—'
                });
            });

            if (!delRows.length && parsedNew?.deleted) {
                delRows.push({
                    section: 'Delete',
                    label: 'Details',
                    oldVal: 'No snapshot was stored for this delete.',
                    newVal: '—'
                });
            }

            return delRows;
        }

        const isCreateAction = isCreateLog;

        // Patient Meter (and similar) often store create/update payloads as arrays of meter rows.
        const oldMeterList = Array.isArray(parsedOld)
            ? parsedOld
            : (Array.isArray(parsedOld?.meters) ? parsedOld.meters : null);
        const newMeterList = Array.isArray(parsedNew)
            ? parsedNew
            : (Array.isArray(parsedNew?.meters) ? parsedNew.meters : null);
        const looksLikeMeterList =
            (Array.isArray(oldMeterList) && (oldMeterList.length === 0 || oldMeterList.some(isMeterMappingRow))) ||
            (Array.isArray(newMeterList) && (newMeterList.length === 0 || newMeterList.some(isMeterMappingRow)));

        if (looksLikeMeterList && (oldMeterList || newMeterList)) {
            const oldArr = Array.isArray(oldMeterList) ? oldMeterList.filter(isMeterMappingRow).map(normalizeMeterRowFields) : [];
            const newArr = Array.isArray(newMeterList) ? newMeterList.filter(isMeterMappingRow).map(normalizeMeterRowFields) : [];
            const oldByKey = new Map(oldArr.map((m) => [meterRowKey(m), m]));
            const newByKey = new Map(newArr.map((m) => [meterRowKey(m), m]));
            const allMeterKeys = new Set([...oldByKey.keys(), ...newByKey.keys()]);

            allMeterKeys.forEach((mKey) => {
                const before = oldByKey.get(mKey);
                const after = newByKey.get(mKey);
                const sectionLabel = after?.serial_number || before?.serial_number
                    ? `Meter ${after?.serial_number || before?.serial_number}`
                    : 'Meter Mapping';

                if (isCreateAction || (!before && after)) {
                    Object.keys(after || {}).forEach((field) => {
                        const newNorm = normalize(field, after[field]);
                        if (newNorm === 'N/A' && !after[field]) return;
                        rows.push({
                            section: sectionLabel,
                            label: formatFieldLabel(field),
                            oldVal: 'N/A',
                            newVal: newNorm
                        });
                    });
                    return;
                }

                if (before && !after) {
                    Object.keys(before).forEach((field) => {
                        const oldNorm = normalize(field, before[field]);
                        if (oldNorm === 'N/A' && !before[field]) return;
                        rows.push({
                            section: sectionLabel,
                            label: formatFieldLabel(field),
                            oldVal: oldNorm,
                            newVal: '—'
                        });
                    });
                    return;
                }

                Object.keys({ ...before, ...after }).forEach((field) => {
                    const oldNorm = normalize(field, before?.[field]);
                    const newNorm = normalize(field, after?.[field]);
                    if (oldNorm === newNorm) return;
                    rows.push({
                        section: sectionLabel,
                        label: formatFieldLabel(field),
                        oldVal: oldNorm,
                        newVal: newNorm
                    });
                });
            });
        } else {
            const createPayload = isCreateAction
                ? (() => {
                    // Prefer update_data (new). Some legacy creates only stored the snapshot in data.
                    const oldObj = (parsedOld && typeof parsedOld === 'object' && !Array.isArray(parsedOld)) ? parsedOld : {};
                    const newObj = (parsedNew && typeof parsedNew === 'object' && !Array.isArray(parsedNew)) ? parsedNew : {};
                    const hasNew = Object.keys(newObj).length > 0;
                    return hasNew ? { ...oldObj, ...newObj } : { ...oldObj };
                })()
                : null;

            if (isCreateAction && createPayload) {
                // Create: every field is Old N/A → New actual value.
                Object.keys(createPayload).forEach((key) => {
                    const newValue = createPayload[key];
                    if (newValue && typeof newValue === 'object' && !Array.isArray(newValue) && ('to' in newValue || 'from' in newValue)) {
                        processChange(key, undefined, newValue.to !== undefined ? newValue.to : newValue.from);
                    } else {
                        processChange(key, undefined, newValue);
                    }
                });
            } else if (isChangesObject) {
                // Direct diff from backend
                Object.entries(parsedNew).forEach(([key, change]) => {
                    if (change && typeof change === 'object' && ('from' in change || 'to' in change)) {
                        processChange(key, change.from, change.to);
                    } else {
                        // Fallback for non-diffed keys
                        processChange(key, parsedOld[key], change);
                    }
                });
            } else if (!Array.isArray(parsedOld) && !Array.isArray(parsedNew)) {
                // Full object comparison
                // For partial updates, only compare keys that were sent in the update request
                const allKeys = (actionLower === 'update')
                    ? new Set(Object.keys(parsedNew))
                    : new Set([...Object.keys(parsedOld), ...Object.keys(parsedNew)]);

                allKeys.forEach(key => {
                    processChange(key, parsedOld[key], parsedNew[key]);
                });
            }
        }

        // Special handling for Person Name if not processed
        const oldName = `${parsedOld.first_name || ''} ${parsedOld.last_name || ''}`.trim();
        const newName = `${parsedNew.first_name || ''} ${parsedNew.last_name || ''}`.trim();
        if (oldName && newName && oldName !== newName && !rows.find(r => r.label.includes('Name'))) {
            rows.unshift({
                label: `${moduleType || 'Entity'} Name`,
                oldVal: oldName,
                newVal: newName
            });
        }

        return rows;
    };

    return (
        <div className="event-log-section-container" id="event-log-section">
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading logs...</span>
                    </div>
                </div>
            ) : error ? (
                <div className="event-log-empty text-danger">{error}</div>
            ) : (
                <fieldset className="log-fieldset">
                    <legend className="log-legend">Event Logs</legend>

                    {logs.length > 0 ? (
                        <div className="log-cards-container">
                            {logs.map((log, index) => {
                                const comparisonRows = getComparisonRows(log.data, log.update_data, log.module || moduleId, log.action);
                                const logModule = String(log.module || moduleId || '').toLowerCase();
                                const actionLower = (normalizeEventLogAction(log.action) || String(log.action || '')).toLowerCase();
                                if (logModule === 'practice' && actionLower === 'update' && comparisonRows.length === 0) {
                                    return null;
                                }
                                return (
                                    <div key={log.id} className="log-card">
                                        <div className="log-seq-badge">
                                            {(page - 1) * pageLimit + index + 1}
                                        </div>

                                        <div className="log-meta-grid">
                                            {/* Left Column */}
                                            <div>
                                                <div className="log-meta-item-row mb-3">
                                                    <span className="log-meta-label">Performed by</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{formatPerformedByLabel(log)}</span>
                                                </div>
                                                <div className="log-meta-item-row mb-3">
                                                    <span className="log-meta-label">Performed on</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{formatPerformedOnLabel(log.module || moduleId, { log })}</span>
                                                </div>
                                                <div className="log-meta-item-row">
                                                    <span className="log-meta-label">Module</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{moduleDisplayName(log.module || moduleId)}</span>
                                                </div>
                                            </div>

                                            {/* Right Column */}
                                            <div>
                                                <div className="log-meta-item-row mb-3">
                                                    <span className="log-meta-label">Action</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{formatActionLabel(log.action)}</span>
                                                </div>
                                                <div className="log-meta-item-row">
                                                    <span className="log-meta-label">Date</span>
                                                    <span className="log-meta-colon">:</span>
                                                    <span className="log-meta-value">{formatDate(log.created_at)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {(() => {
                                            const actionLower = normalizeEventLogAction(log.action);
                                            if (actionLower === 'read' || actionLower === 'view') {
                                                const readData = parseEventLogJson(log.update_data);
                                                const isPracticeModule = String(log.module || moduleId || '').toLowerCase() === 'practice';
                                                const readRows = (isPracticeModule
                                                    ? [
                                                        ['Practice Name', readData.practice_name],
                                                        ['Status', readData.status],
                                                    ]
                                                    : [
                                                        ['First Name', readData.first_name],
                                                        ['Last Name', readData.last_name],
                                                        ['Username', readData.username],
                                                    ]
                                                ).filter(([, value]) => value != null && String(value).trim() !== '');

                                                return (
                                                    <div className="log-delete-block">
                                                        <div className="log-data-box log-delete-snapshot-box">
                                                            <div className="log-data-header log-read-snapshot-header">
                                                                Record viewed
                                                            </div>
                                                            <div className="log-data-content">
                                                                {readRows.length > 0 ? (
                                                                    <table className="log-data-table">
                                                                        <tbody>
                                                                            {readRows.map(([label, value]) => (
                                                                                <tr key={label}>
                                                                                    <td className="log-data-key-cell">{label}</td>
                                                                                    <td className="log-data-val-cell">{value}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <div className="log-delete-no-snapshot">
                                                                        Record was opened for viewing.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            if (actionLower === 'export_pdf' || actionLower === 'export_csv' || actionLower === 'email_report' || actionLower === 'email') {
                                                const exportData = typeof log.update_data === 'string'
                                                    ? JSON.parse(log.update_data || '{}')
                                                    : (log.update_data || {});
                                                const isEmail = actionLower === 'email_report' || actionLower === 'email';
                                                const exportRows = [
                                                    ['Format', exportData.format || (actionLower === 'export_csv' ? 'CSV' : isEmail ? 'Email' : 'PDF')],
                                                    ['Source', exportData.source],
                                                ].filter(([, value]) => value != null && String(value).trim() !== '');

                                                return (
                                                    <div className="log-delete-block">
                                                        <div className="log-data-box log-delete-snapshot-box">
                                                            <div className="log-data-header log-read-snapshot-header">
                                                                {isEmail
                                                                  ? 'Email report sent'
                                                                  : actionLower === 'export_csv'
                                                                    ? 'CSV exported'
                                                                    : 'PDF exported'}
                                                            </div>
                                                            <div className="log-data-content">
                                                                {exportRows.length > 0 ? (
                                                                    <table className="log-data-table">
                                                                        <tbody>
                                                                            {exportRows.map(([label, value]) => (
                                                                                <tr key={label}>
                                                                                    <td className="log-data-key-cell">{label}</td>
                                                                                    <td className="log-data-val-cell">{value}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <div className="log-delete-no-snapshot">
                                                                        {isEmail ? 'Email report was recorded.' : 'Export was recorded.'}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            if (actionLower === 'delete') {
                                                const deleteRows = getComparisonRows(
                                                    log.data,
                                                    log.update_data,
                                                    log.module || moduleId,
                                                    log.action
                                                );
                                                const deleteSections = [...new Set(deleteRows.map((r) => r.section))];

                                                return (
                                                    <div className="log-delete-block">
                                                        {deleteRows.length > 0 ? (
                                                            <div className="log-data-box log-delete-snapshot-box">
                                                                <div className="log-data-header log-delete-snapshot-header">
                                                                    Record deleted
                                                                </div>
                                                                <div className="log-data-content">
                                                                    {deleteSections.map((section) => (
                                                                        <div key={section} className="log-change-section">
                                                                            <div className="log-section-title">{section}</div>
                                                                            <table className="log-data-table">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th className="log-data-key-cell log-delete-th">Field</th>
                                                                                        <th className="log-data-val-cell log-delete-th">Value</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {deleteRows
                                                                                        .filter((r) => r.section === section)
                                                                                        .map((row, idx) => (
                                                                                            <tr key={idx}>
                                                                                                <td className="log-data-key-cell">{row.label}</td>
                                                                                                <td className="log-data-val-cell">{row.oldVal}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="log-delete-no-snapshot">
                                                                No field snapshot was stored for this delete.
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }

                                            if (comparisonRows.length === 0) {
                                                return (
                                                    <div className="log-no-changes-alert">
                                                        No specific field changes recorded for this event.
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="log-data-comparison-container">
                                                    {/* Old Data Box */}
                                                    <div className="log-data-box">
                                                        <div className="log-data-header">Old Data</div>
                                                        <div className="log-data-content">
                                                            {(() => {
                                                                const sections = [...new Set(comparisonRows.map(r => r.section))];
                                                                return sections.map(section => (
                                                                    <div key={section} className="log-change-section">
                                                                        <div className="log-section-title">{section}</div>
                                                                        <table className="log-data-table">
                                                                            <tbody>
                                                                                {comparisonRows.filter(r => r.section === section).map((row, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td className="log-data-key-cell">{row.label}</td>
                                                                                        <td className="log-data-val-cell">{row.oldVal}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </div>

                                                    {/* New Data Box */}
                                                    <div className="log-data-box">
                                                        <div className="log-data-header">New Data</div>
                                                        <div className="log-data-content">
                                                            {(() => {
                                                                const sections = [...new Set(comparisonRows.map(r => r.section))];
                                                                return sections.map(section => (
                                                                    <div key={section} className="log-change-section">
                                                                        <div className="log-section-title">{section}</div>
                                                                        <table className="log-data-table">
                                                                            <tbody>
                                                                                {comparisonRows.filter(r => r.section === section).map((row, idx) => (
                                                                                    <tr key={idx}>
                                                                                        <td className="log-data-key-cell">{row.label}</td>
                                                                                        <td className="log-data-val-cell log-new-val-highlight">{row.newVal}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="event-log-empty">
                            <i className="fas fa-file-alt fa-3x mb-3 text-muted"></i>
                            <p>
                                {allModules || modulePrefix
                                    ? (patientId
                                        ? 'No event logs found for this patient yet. Profile views, edits, follow-ups, meters, documents, vitals, and exports will appear here.'
                                        : 'No event logs found yet. Changes recorded across practices and modules will appear here.')
                                    : String(moduleId).toLowerCase() === 'billing'
                                        ? 'No billing event logs found yet. Viewing reports, exports, emails, and billing date changes will appear here.'
                                        : String(moduleId).toLowerCase() === 'practice'
                                            ? 'No practice event logs found yet. Opening a practice, edits, and deletions will appear here.'
                                        : `No logs found for this ${moduleDisplayName(moduleId)}. Modifying data will generate logs here.`}
                            </p>
                        </div>
                    )}

                    {totalLogs > 0 && (
                        <ListScreenPaginationBar
                            entriesValue={entriesPerPage}
                            onEntriesChange={handleEntriesChange}
                            currentPage={page}
                            totalRecords={totalLogs}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    )}
                </fieldset>
            )}
        </div>
    );
};

export default EventLogSection;
